import { useHistory, useLocation } from "react-router";
import { parse } from "qs";
import React, { useEffect, useState } from "react";
import { stringifyMinimal } from "../../utils/browser";
import { omit } from "lodash-es";
import { BugIcon } from "./DebugFeature";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { DebugQueryParameterName } from "../../types/debug";

export default function useDebugFeature({
  queryParameterName,
}: DebugFeatureOptions) {
  const location = useLocation();
  const history = useHistory();
  const parsedQs = parse(location?.search ?? "", { ignoreQueryPrefix: true });
  const queryParameterSet = queryParameterName in parsedQs;
  const localStorageKey = `debug-${queryParameterName}`;
  const inLocalStorage = !!localStorage.getItem(localStorageKey);
  const show = inLocalStorage || queryParameterSet;
  const [, setRandomState] = useState(() => Math.random());
  const rerender = () => setRandomState(Math.random());
  useEffect(() => {
    if (queryParameterSet) {
      localStorage.setItem(localStorageKey, "1");
      rerender();
    }
  }, [localStorageKey, queryParameterSet]);

  if (!show) {
    return null;
  }

  const closePermanently = () => {
    localStorage.removeItem(localStorageKey);
    rerender();
    if (queryParameterSet) {
      // Get taken to the same URL, but without the query parameter.
      history.push({
        ...history.location,
        search: stringifyMinimal(omit(parsedQs, queryParameterName), {
          addQueryPrefix: true,
        }),
      });
    }
  };

  return {
    bugIcon: <BugIcon queryParameterName={queryParameterName} />,
    closeIcon: (
      <FontAwesomeIcon
        icon={faTimes}
        title={`Click to hide this debug feature until ?${queryParameterName} is used again.`}
        onClick={closePermanently}
        className="clickable"
      />
    ),
    closePermanently,
  };
}

export type DebugFeatureOptions = {
  queryParameterName: DebugQueryParameterName;
};
